import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import type { IdeaTask, TaskStatus, IdeaPlanMeta } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { 
  AlertCircle,
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  Minus,
  Plus,
  RotateCcw,
  Maximize2,
  GripVertical,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format, differenceInDays, addDays, parseISO, isAfter, isBefore } from 'date-fns';
import { toast } from 'sonner';

interface PlanTimelineProps {
  tasks: IdeaTask[];
  planMeta: IdeaPlanMeta | null;
  onUpdateTask: (taskId: string, updates: Partial<IdeaTask>) => Promise<boolean>;
}

const STATUS_CONFIG: Record<TaskStatus, { icon: typeof Circle; className: string; barClass: string }> = {
  next: { icon: Circle, className: 'text-muted-foreground', barClass: 'bg-muted-foreground/60' },
  doing: { icon: Clock, className: 'text-primary', barClass: 'bg-primary' },
  blocked: { icon: AlertCircle, className: 'text-destructive', barClass: 'bg-destructive/70' },
  done: { icon: CheckCircle2, className: 'text-primary', barClass: 'bg-primary/50' },
};

// Zoom levels in pixels per day
const ZOOM_LEVELS = [8, 12, 18, 26, 36];
const DEFAULT_ZOOM_INDEX = 2; // 18px/day
const DEFAULT_HEIGHT = 380;
const EXPANDED_HEIGHT_VH = 75;

const STORAGE_KEYS = {
  zoom: 'yelv.timelineZoom',
  height: 'yelv.timelineHeight',
  expanded: 'yelv.timelineExpanded',
};

type DragMode = 'move' | 'resize-start' | 'resize-end' | null;

interface DragState {
  taskId: string;
  mode: DragMode;
  initialMouseX: number;
  initialStartDate: Date;
  initialDueDate: Date;
  currentDeltaDays: number;
}

export function PlanTimeline({ tasks, planMeta, onUpdateTask }: PlanTimelineProps) {
  const [dragState, setDragState] = useState<DragState | null>(null);
  
  // Zoom state
  const [zoomIndex, setZoomIndex] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.zoom);
    const parsed = saved ? parseInt(saved, 10) : NaN;
    return !isNaN(parsed) && parsed >= 0 && parsed < ZOOM_LEVELS.length ? parsed : DEFAULT_ZOOM_INDEX;
  });
  
  // Height state
  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.expanded);
    return saved === 'true';
  });
  
  const [customHeight, setCustomHeight] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.height);
    const parsed = saved ? parseInt(saved, 10) : NaN;
    return !isNaN(parsed) ? parsed : DEFAULT_HEIGHT;
  });
  
  const [isResizingHeight, setIsResizingHeight] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const timelineContentRef = useRef<HTMLDivElement>(null);
  
  const pxPerDay = ZOOM_LEVELS[zoomIndex];

  // Persist zoom to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.zoom, zoomIndex.toString());
  }, [zoomIndex]);
  
  // Persist height state to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.expanded, isExpanded.toString());
  }, [isExpanded]);
  
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.height, customHeight.toString());
  }, [customHeight]);

  // Calculate timeline bounds
  const { startDate, endDate, totalDays } = useMemo(() => {
    const planStart = planMeta?.plan_start_date ? parseISO(planMeta.plan_start_date) : new Date();
    const planEnd = planMeta?.plan_end_date ? parseISO(planMeta.plan_end_date) : addDays(new Date(), 28);
    
    // Extend if tasks fall outside
    let minDate = planStart;
    let maxDate = planEnd;
    
    tasks.forEach(t => {
      const start = t.start_date ? parseISO(t.start_date) : (t.due_date ? parseISO(t.due_date) : null);
      const due = t.due_date ? parseISO(t.due_date) : start;
      
      if (start && isBefore(start, minDate)) minDate = start;
      if (due && isAfter(due, maxDate)) maxDate = due;
    });
    
    const days = differenceInDays(maxDate, minDate) + 1;
    return {
      startDate: minDate,
      endDate: maxDate,
      totalDays: Math.max(days, 7),
    };
  }, [tasks, planMeta]);

  // Calculate total width based on zoom
  const timelineWidth = totalDays * pxPerDay;

  // Filter tasks with dates and normalize missing start_date
  const timelineTasks = useMemo(() => {
    return tasks
      .filter(t => t.due_date || t.start_date)
      .map(t => ({
        ...t,
        // Default start_date to due_date if missing (1-day task)
        start_date: t.start_date || t.due_date,
        due_date: t.due_date || t.start_date,
      }))
      .sort((a, b) => {
        const aStart = parseISO(a.start_date!);
        const bStart = parseISO(b.start_date!);
        return aStart.getTime() - bStart.getTime();
      });
  }, [tasks]);

  const getTaskPosition = useCallback((task: IdeaTask, deltaDays = 0, mode: DragMode = null) => {
    if (!task.start_date || !task.due_date) return null;
    
    let taskStart = parseISO(task.start_date);
    let taskEnd = parseISO(task.due_date);
    
    // Apply drag delta based on mode
    if (deltaDays !== 0) {
      if (mode === 'move') {
        taskStart = addDays(taskStart, deltaDays);
        taskEnd = addDays(taskEnd, deltaDays);
      } else if (mode === 'resize-start') {
        taskStart = addDays(taskStart, deltaDays);
        // Ensure start <= end (minimum 1 day)
        if (isAfter(taskStart, taskEnd)) {
          taskStart = taskEnd;
        }
      } else if (mode === 'resize-end') {
        taskEnd = addDays(taskEnd, deltaDays);
        // Ensure end >= start (minimum 1 day)
        if (isBefore(taskEnd, taskStart)) {
          taskEnd = taskStart;
        }
      }
    }
    
    const startOffset = differenceInDays(taskStart, startDate);
    const duration = differenceInDays(taskEnd, taskStart) + 1;
    
    return {
      left: startOffset * pxPerDay,
      width: Math.max(duration * pxPerDay, pxPerDay), // Minimum 1 day width
      startDate: taskStart,
      endDate: taskEnd,
    };
  }, [startDate, pxPerDay]);

  const isOutsidePlanWindow = useCallback((taskStartDate: Date, taskEndDate: Date): boolean => {
    if (!planMeta?.plan_start_date || !planMeta?.plan_end_date) return false;
    
    const planStart = parseISO(planMeta.plan_start_date);
    const planEnd = parseISO(planMeta.plan_end_date);
    
    return isBefore(taskStartDate, planStart) || isAfter(taskEndDate, planEnd);
  }, [planMeta]);

  // Zoom handlers
  const handleZoomIn = () => {
    setZoomIndex(prev => Math.min(prev + 1, ZOOM_LEVELS.length - 1));
  };
  
  const handleZoomOut = () => {
    setZoomIndex(prev => Math.max(prev - 1, 0));
  };
  
  const handleZoomReset = () => {
    setZoomIndex(DEFAULT_ZOOM_INDEX);
  };
  
  const handleFitToWindow = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const containerWidth = scrollContainerRef.current.clientWidth - 32;
    const idealPxPerDay = containerWidth / totalDays;
    
    const closestIndex = ZOOM_LEVELS.reduce((closest, level, index) => {
      const currentDiff = Math.abs(level - idealPxPerDay);
      const closestDiff = Math.abs(ZOOM_LEVELS[closest] - idealPxPerDay);
      return currentDiff < closestDiff ? index : closest;
    }, 0);
    
    setZoomIndex(closestIndex);
  }, [totalDays]);

  // Height toggle
  const toggleExpanded = () => {
    setIsExpanded(prev => !prev);
  };

  // Drag-to-resize height handle
  const handleHeightResizeStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsResizingHeight(true);
    
    const startY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const startHeight = customHeight;
    
    const handleMove = (moveEvent: MouseEvent | TouchEvent) => {
      const currentY = 'touches' in moveEvent ? moveEvent.touches[0].clientY : moveEvent.clientY;
      const deltaY = currentY - startY;
      const newHeight = Math.max(200, Math.min(800, startHeight + deltaY));
      setCustomHeight(newHeight);
    };
    
    const handleEnd = () => {
      setIsResizingHeight(false);
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
    
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  };

  // Task drag handlers
  const handleTaskDragStart = useCallback((
    e: React.MouseEvent | React.TouchEvent,
    taskId: string,
    mode: DragMode
  ) => {
    e.preventDefault();
    e.stopPropagation();
    
    const task = timelineTasks.find(t => t.id === taskId);
    if (!task || !task.start_date || !task.due_date) return;
    
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    
    setDragState({
      taskId,
      mode,
      initialMouseX: clientX,
      initialStartDate: parseISO(task.start_date),
      initialDueDate: parseISO(task.due_date),
      currentDeltaDays: 0,
    });
  }, [timelineTasks]);

  // Global drag move/end handlers
  useEffect(() => {
    if (!dragState) return;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const deltaPixels = clientX - dragState.initialMouseX;
      const deltaDays = Math.round(deltaPixels / pxPerDay);
      
      setDragState(prev => prev ? { ...prev, currentDeltaDays: deltaDays } : null);
    };

    const handleEnd = async () => {
      if (!dragState || dragState.currentDeltaDays === 0) {
        setDragState(null);
        return;
      }

      const { taskId, mode, initialStartDate, initialDueDate, currentDeltaDays } = dragState;
      
      let newStart = initialStartDate;
      let newEnd = initialDueDate;
      
      if (mode === 'move') {
        newStart = addDays(initialStartDate, currentDeltaDays);
        newEnd = addDays(initialDueDate, currentDeltaDays);
      } else if (mode === 'resize-start') {
        newStart = addDays(initialStartDate, currentDeltaDays);
        if (isAfter(newStart, newEnd)) newStart = newEnd;
      } else if (mode === 'resize-end') {
        newEnd = addDays(initialDueDate, currentDeltaDays);
        if (isBefore(newEnd, newStart)) newEnd = newStart;
      }

      // Check for plan drift
      if (isOutsidePlanWindow(newStart, newEnd)) {
        toast.warning('Plan drift detected', {
          description: 'This task now extends outside your plan window.',
        });
      }

      // Persist to database
      const success = await onUpdateTask(taskId, {
        start_date: format(newStart, 'yyyy-MM-dd'),
        due_date: format(newEnd, 'yyyy-MM-dd'),
      });

      if (!success) {
        toast.error('Failed to update task dates');
      }

      setDragState(null);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [dragState, pxPerDay, onUpdateTask, isOutsidePlanWindow]);

  // Calculate dynamic height
  const containerHeight = isExpanded ? `${EXPANDED_HEIGHT_VH}vh` : `${customHeight}px`;

  // Generate day/week markers based on zoom level
  const markers = useMemo(() => {
    const result = [];
    const markerInterval = pxPerDay >= 18 ? 7 : 14;
    for (let i = 0; i < totalDays; i += markerInterval) {
      const date = addDays(startDate, i);
      result.push({
        left: i * pxPerDay,
        label: format(date, 'MMM d'),
      });
    }
    return result;
  }, [startDate, totalDays, pxPerDay]);

  // Plan window position
  const planWindowStyle = useMemo(() => {
    if (!planMeta?.plan_start_date || !planMeta?.plan_end_date) return null;
    return {
      left: differenceInDays(parseISO(planMeta.plan_start_date), startDate) * pxPerDay,
      width: (differenceInDays(parseISO(planMeta.plan_end_date), parseISO(planMeta.plan_start_date)) + 1) * pxPerDay,
    };
  }, [planMeta, startDate, pxPerDay]);

  // Get current drag preview position
  const getDragPreview = useCallback((task: IdeaTask) => {
    if (!dragState || dragState.taskId !== task.id) return null;
    return getTaskPosition(task, dragState.currentDeltaDays, dragState.mode);
  }, [dragState, getTaskPosition]);

  if (timelineTasks.length === 0) {
    return (
      <p className="text-sm text-muted-foreground/60 text-center py-8 italic">
        No tasks with dates to display.
      </p>
    );
  }

  return (
    <div className="flex flex-col" ref={containerRef}>
      {/* Timeline header with controls */}
      <div className="flex items-center justify-between gap-2 pb-3 border-b mb-3">
        <div className="flex items-center gap-2">
          {/* Zoom controls */}
          <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-0.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={handleZoomOut}
              disabled={zoomIndex === 0}
              title="Zoom out"
            >
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <span className="text-[10px] text-muted-foreground w-10 text-center">
              {pxPerDay}px/d
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={handleZoomIn}
              disabled={zoomIndex === ZOOM_LEVELS.length - 1}
              title="Zoom in"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={handleZoomReset}
            title="Reset zoom"
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={handleFitToWindow}
            title="Fit to window"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
        </div>
        
        {/* Expand/collapse toggle */}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={toggleExpanded}
          title={isExpanded ? 'Collapse timeline' : 'Expand timeline'}
        >
          {isExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Scrollable timeline container */}
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto overflow-y-auto transition-all duration-200"
        style={{ height: containerHeight }}
      >
        <div 
          ref={timelineContentRef}
          style={{ width: Math.max(timelineWidth, 100), minWidth: '100%' }}
        >
          {/* Date markers header */}
          <div className="relative h-6 border-b sticky top-0 bg-background z-10">
            {markers.map((marker, i) => (
              <div 
                key={i}
                className="absolute text-[10px] text-muted-foreground whitespace-nowrap"
                style={{ left: marker.left }}
              >
                {marker.label}
              </div>
            ))}
          </div>

          {/* Timeline rows */}
          <div className="relative pt-2 space-y-1">
            {/* Plan window indicator */}
            {planWindowStyle && (
              <div 
                className="absolute top-0 bottom-0 bg-primary/5 border-x border-primary/20 pointer-events-none z-0"
                style={{
                  left: planWindowStyle.left,
                  width: planWindowStyle.width,
                }}
              />
            )}

            {/* Drag guide line */}
            {dragState && (
              <div 
                className="absolute top-0 bottom-0 w-px bg-primary/60 pointer-events-none z-20"
                style={{
                  left: (() => {
                    const task = timelineTasks.find(t => t.id === dragState.taskId);
                    if (!task) return 0;
                    const preview = getDragPreview(task);
                    if (!preview) return 0;
                    return dragState.mode === 'resize-end' ? preview.left + preview.width : preview.left;
                  })(),
                }}
              />
            )}

            {timelineTasks.map((task) => {
              const basePos = getTaskPosition(task);
              if (!basePos) return null;

              const isDragging = dragState?.taskId === task.id;
              const preview = isDragging ? getDragPreview(task) : null;
              const displayPos = preview || basePos;
              
              const StatusIcon = STATUS_CONFIG[task.status].icon;
              const outsideWindow = isOutsidePlanWindow(displayPos.startDate, displayPos.endDate);

              return (
                <div key={task.id} className="relative h-10 flex items-center">
                  {/* Task bar */}
                  <div
                    className={cn(
                      "absolute h-7 rounded-md flex items-center gap-1 select-none group",
                      STATUS_CONFIG[task.status].barClass,
                      task.status === 'done' && "opacity-60",
                      isDragging && "ring-2 ring-primary shadow-lg z-10",
                      outsideWindow && "ring-1 ring-amber-500",
                      "transition-shadow"
                    )}
                    style={{
                      left: displayPos.left,
                      width: Math.max(displayPos.width, pxPerDay),
                    }}
                  >
                    {/* Left resize handle */}
                    <div
                      className={cn(
                        "absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize flex items-center justify-center",
                        "opacity-0 group-hover:opacity-100 transition-opacity",
                        isDragging && dragState?.mode === 'resize-start' && "opacity-100"
                      )}
                      onMouseDown={(e) => handleTaskDragStart(e, task.id, 'resize-start')}
                      onTouchStart={(e) => handleTaskDragStart(e, task.id, 'resize-start')}
                    >
                      <GripVertical className="h-3 w-3 text-white/60" />
                    </div>
                    
                    {/* Main bar content (draggable for move) */}
                    <div
                      className="flex-1 h-full flex items-center gap-1.5 px-2 cursor-move overflow-hidden"
                      onMouseDown={(e) => handleTaskDragStart(e, task.id, 'move')}
                      onTouchStart={(e) => handleTaskDragStart(e, task.id, 'move')}
                    >
                      <StatusIcon className="h-3 w-3 shrink-0 text-white/80" />
                      <span className={cn(
                        "text-[10px] font-medium text-white truncate",
                        task.status === 'done' && "line-through"
                      )}>
                        {task.title}
                      </span>
                      {outsideWindow && (
                        <AlertTriangle className="h-3 w-3 text-amber-300 shrink-0 ml-auto" />
                      )}
                    </div>
                    
                    {/* Right resize handle */}
                    <div
                      className={cn(
                        "absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize flex items-center justify-center",
                        "opacity-0 group-hover:opacity-100 transition-opacity",
                        isDragging && dragState?.mode === 'resize-end' && "opacity-100"
                      )}
                      onMouseDown={(e) => handleTaskDragStart(e, task.id, 'resize-end')}
                      onTouchStart={(e) => handleTaskDragStart(e, task.id, 'resize-end')}
                    >
                      <GripVertical className="h-3 w-3 text-white/60" />
                    </div>

                    {/* Drag tooltip */}
                    {isDragging && preview && (
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-popover border rounded shadow-lg text-[10px] font-medium whitespace-nowrap z-30">
                        {format(preview.startDate, 'MMM d')} → {format(preview.endDate, 'MMM d')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Resize handle (only when not expanded) */}
      {!isExpanded && (
        <div
          className={cn(
            "h-2 mt-1 cursor-row-resize flex items-center justify-center group touch-none",
            isResizingHeight && "bg-primary/10"
          )}
          onMouseDown={handleHeightResizeStart}
          onTouchStart={handleHeightResizeStart}
        >
          <div className="w-12 h-1 rounded-full bg-muted-foreground/20 group-hover:bg-muted-foreground/40 transition-colors" />
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-4 pt-3 text-[10px] text-muted-foreground border-t mt-2">
        <div className="flex items-center gap-1">
          <div className="w-3 h-2 bg-primary/5 border border-primary/20 rounded-sm" />
          <span>Plan window</span>
        </div>
        <div className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 text-amber-500" />
          <span>Plan drift</span>
        </div>
        <div className="flex items-center gap-1 ml-2">
          <GripVertical className="h-3 w-3 text-muted-foreground/50" />
          <span>Drag to resize</span>
        </div>
        <span className="ml-auto text-muted-foreground/50">
          {totalDays} days · {timelineTasks.length} tasks
        </span>
      </div>
    </div>
  );
}
